import * as React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SortDef, SortOrder } from './types';

interface SortControlProps {
  sorts: SortDef[];
  activeField: string | null;
  activeOrder: SortOrder;
  isDefault: boolean;
  onSelect: (field: string, order: SortOrder) => void;
  onClear: () => void;
}

/**
 * Toolbar sort badge: shows the effective sort (field + direction) with a sort
 * icon to distinguish it from filters. Active (non-default) sorts get a clear X.
 */
const SortControl: React.FC<SortControlProps> = ({
  sorts,
  activeField,
  activeOrder,
  isDefault,
  onSelect,
  onClear,
}) => {
  const [open, setOpen] = React.useState(false);
  const activeDef = sorts.find((s) => s.field === activeField);
  const DirIcon = activeOrder === 'asc' ? ArrowUp : ArrowDown;

  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'h-9 gap-1.5 rounded-lg px-3 font-normal text-sm',
              !isDefault && 'border-primary/40 bg-primary/5',
            )}
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            {activeDef ? (
              <span className="flex items-center gap-1">
                <span className="text-muted-foreground">Sort:</span>
                <span className="max-w-[140px] truncate font-medium">{activeDef.label}</span>
                <DirIcon className="h-3.5 w-3.5" />
              </span>
            ) : (
              <span>Sort</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-1" align="end">
          {sorts.map((s) => {
            const isActive = s.field === activeField;
            const RowDir = activeOrder === 'asc' ? ArrowUp : ArrowDown;
            return (
              <button
                key={s.id}
                type="button"
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-accent text-left"
                onClick={() => {
                  const order: SortOrder = isActive
                    ? activeOrder === 'asc'
                      ? 'desc'
                      : 'asc'
                    : s.defaultOrder ?? 'desc';
                  onSelect(s.field, order);
                  setOpen(false);
                }}
              >
                <Check className={cn('h-4 w-4 shrink-0', isActive ? 'opacity-100' : 'opacity-0')} />
                <span className="flex-1 truncate">{s.label}</span>
                {isActive ? <RowDir className="h-3.5 w-3.5 opacity-70" /> : null}
              </button>
            );
          })}
        </PopoverContent>
      </Popover>
      {!isDefault && (
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-7 text-muted-foreground hover:text-destructive"
          onClick={onClear}
          aria-label="Clear sort"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
};

export default SortControl;
